import { createSlice, PayloadAction, createSelector } from '@reduxjs/toolkit'

import { EventData, EventStatus, GameEventId } from '~/gamification/types/gamification.types'
import { rewardApi } from '~/redux/apis/reward-api'

import { RootState } from '~/redux/store'

// Mirrors `logout` in `~/auth/slices/auth.slice`; pinned by the useHasLevelUp logout test.
const LOGOUT_ACTION_TYPE = 'auth/logout'

interface GamificationState {
  events: EventData[] | undefined
  funfact?: unknown[]
  mathEnergyOrbs?: number
  notification: boolean
  isNotificationListOpen: boolean
  points?: number
  gems: number
  rewardsCount: { initial: number; collected: number; available: number }
  nextBootcampPage: { currentActivityType: number }
  hasFetchedEvents: boolean
  hasLeveledUp: boolean
  hasLoggedOut: boolean
  fromRetakeQuiz: boolean
  showHomebaseClosingX: boolean
  showShopButton: boolean
  isQuizFinished?: boolean
  leaderboardPosition?: number
  hasShownLeaderboardModal: boolean
}

const initialState: GamificationState = {
  events: undefined,
  funfact: undefined,
  mathEnergyOrbs: undefined,
  notification: false,
  isNotificationListOpen: false,
  points: undefined,
  gems: 0,
  rewardsCount: { initial: 0, collected: 0, available: 0 },
  nextBootcampPage: { currentActivityType: 0 },
  hasFetchedEvents: false,
  hasLeveledUp: false,
  hasLoggedOut: false,
  fromRetakeQuiz: false,
  showHomebaseClosingX: true,
  showShopButton: true,
  isQuizFinished: false,
  hasShownLeaderboardModal: false,
}

const slice = createSlice({
  name: 'gamification',
  initialState,
  reducers: {
    updateEvents: (
      state,
      action: PayloadAction<{
        events: EventData[]
        reduxOnly?: boolean
      }>,
    ) => {
      const currentEvents = state.events || []
      const { events, reduxOnly } = action.payload

      const shouldUpdateEvents = events.some(
        (event) =>
          !currentEvents.some(
            (currentEvent) =>
              currentEvent.id === event.id &&
              currentEvent.status === event.status &&
              currentEvent.stepsCompleted === event.stepsCompleted,
          ),
      )

      let updatedEvents
      if (shouldUpdateEvents) {
        const newEvents = events.filter((event) => !currentEvents.some((currentEvent) => currentEvent.id === event.id))

        const mappedEvents = currentEvents.map(
          (currentEvent) => events.find((event) => event.id === currentEvent.id) || currentEvent,
        )

        updatedEvents = [...mappedEvents, ...newEvents]
      }

      const finishedEvents = events.filter(({ id, status }) => {
        return (
          status === EventStatus.COMPLETED &&
          !currentEvents.some((event) => event.id === id && event.status === EventStatus.COMPLETED)
        )
      })
      const extraPoints = reduxOnly
        ? 0
        : finishedEvents.reduce((acc, item) => {
            return item ? acc + (item.points || 0) : acc
          }, 0)

      return {
        ...state,
        events: updatedEvents || state.events,
        // Never invent a total: before /v1/reward answers, `points` is unknown, and `?? 0` turned
        // that into a real 0 — which reads as level 1 and latched a false level-up baseline.
        points: state.points === undefined ? undefined : state.points + extraPoints,
      }
    },
    resetGamification: (state) => ({
      ...state,
      ...initialState,
    }),
    setEvents: (state, action: PayloadAction<EventData[]>) => ({
      ...state,
      events: action.payload,
    }),
    setFunfact: (state, action: PayloadAction<GamificationState['funfact']>) => ({
      ...state,
      funfact: action.payload,
    }),
    setMathEnergyOrbs: (state, action: PayloadAction<GamificationState['mathEnergyOrbs']>) => ({
      ...state,
      mathEnergyOrbs: action.payload,
    }),
    setNotification: (state, action: PayloadAction<GamificationState['notification']>) => ({
      ...state,
      notification: action.payload,
    }),
    setIsNotificationListOpen: (state, action: PayloadAction<GamificationState['isNotificationListOpen']>) => ({
      ...state,
      isNotificationListOpen: action.payload,
    }),
    setPoints: (state, action: PayloadAction<GamificationState['points']>) => ({
      ...state,
      points: action.payload,
    }),
    setHasLeveledUp: (state, action: PayloadAction<GamificationState['hasLeveledUp']>) => ({
      ...state,
      hasLeveledUp: action.payload,
    }),
    setHasLoggedOut: (state, action: PayloadAction<GamificationState['hasLoggedOut']>) => ({
      ...state,
      hasLoggedOut: action.payload,
    }),
    setShowHomebaseClosingX: (state, action: PayloadAction<GamificationState['showHomebaseClosingX']>) => ({
      ...state,
      showHomebaseClosingX: action.payload,
    }),
    setShowShopButton: (state, action: PayloadAction<GamificationState['showShopButton']>) => ({
      ...state,
      showShopButton: action.payload,
    }),
    setGems: (state, action: PayloadAction<GamificationState['gems']>) => ({
      ...state,
      gems: action.payload,
    }),
    setRewardsCount: (state, action: PayloadAction<GamificationState['rewardsCount']>) => ({
      ...state,
      rewardsCount: action.payload,
    }),
    setNextBootcampPage: (state, action: PayloadAction<GamificationState['nextBootcampPage']>) => ({
      ...state,
      nextBootcampPage: action.payload,
    }),
    setIsQuizFinished: (state, action: PayloadAction<GamificationState['isQuizFinished']>) => ({
      ...state,
      isQuizFinished: action.payload,
    }),
    setLeaderboardPosition: (state, action: PayloadAction<GamificationState['leaderboardPosition']>) => ({
      ...state,
      leaderboardPosition: action.payload,
    }),
    setHasShownLeaderboardModal: (state, action: PayloadAction<GamificationState['hasShownLeaderboardModal']>) => ({
      ...state,
      hasShownLeaderboardModal: action.payload,
    }),
  },
  extraReducers: (builder) => {
    builder
      // `hasLoggedOut` is what rebaselines `useHasLevelUp`. Keyed to the logout action, not to a
      // login hook, so session restore and profile switching rebaseline too. Matched by type rather
      // than by importing `logout`: this slice sits under `reward-api`, so importing `auth.slice`
      // here closes an init cycle through `accountAnalytics` -> `store`.
      .addMatcher(
        (action: { type: string }) => action.type === LOGOUT_ACTION_TYPE,
        () => ({ ...initialState, hasLoggedOut: true }),
      )
      .addMatcher(rewardApi.endpoints.getRewards.matchFulfilled, (state, { payload }) => {
        state.gems = payload.gems
        state.mathEnergyOrbs = payload.kudos
        state.points = payload.xp
      })
  },
})

export const selectEvents = (state: RootState): GamificationState['events'] => {
  return state.gamification.events
}

export const hasFetchedEvents = (state: RootState): GamificationState['hasFetchedEvents'] => {
  return Boolean(state.gamification.events)
}

export const selectMathEnergyOrbs = (state: RootState): GamificationState['mathEnergyOrbs'] => {
  return state.gamification.mathEnergyOrbs
}

export const selectNotification = (state: RootState): GamificationState['notification'] => {
  return state.gamification.notification
}

export const selectIsNotificationListOpen = (state: RootState): GamificationState['isNotificationListOpen'] => {
  return state.gamification.isNotificationListOpen
}

export const selectGems = (state: RootState): GamificationState['gems'] => {
  return state.gamification.gems
}

export const selectPoints = (state: RootState): GamificationState['points'] => {
  return state.gamification.points
}

export const hasLeveledUp = (state: RootState): GamificationState['hasLeveledUp'] => {
  return state.gamification.hasLeveledUp
}
export const hasLoggedOut = (state: RootState): GamificationState['hasLoggedOut'] => {
  return state.gamification.hasLoggedOut
}
export const showHomebaseClosingX = (state: RootState): GamificationState['showHomebaseClosingX'] => {
  return state.gamification.showHomebaseClosingX
}

export const showShopButton = (state: RootState): GamificationState['showShopButton'] => {
  return state.gamification.showShopButton
}

export const selectFunfact = (state: RootState): GamificationState['funfact'] => {
  return state.gamification.funfact
}

export const selectRewardsCount = (state: RootState): GamificationState['rewardsCount'] => {
  return state.gamification.rewardsCount
}

export const selectNextBootcampPage = (state: RootState): GamificationState['nextBootcampPage'] => {
  return state.gamification.nextBootcampPage
}

export const selectFromRetakeQuiz = (state: RootState): GamificationState['fromRetakeQuiz'] => {
  return Boolean(state.gamification.fromRetakeQuiz)
}
export const selectIsQuizFinished = (state: RootState): GamificationState['isQuizFinished'] => {
  return Boolean(state.gamification.isQuizFinished)
}
export const selectLeaderboardPosition = (state: RootState): GamificationState['leaderboardPosition'] => {
  return state.gamification.leaderboardPosition
}
export const selectHasShownLeaderboardModal = (state: RootState): GamificationState['hasShownLeaderboardModal'] => {
  return state.gamification.hasShownLeaderboardModal
}

export const selectMappingQuizStatus = createSelector([selectEvents], (events) => {
  const hasQuiz = !events?.some(({ id, status }) => {
    return id === GameEventId.QUIZ && status === EventStatus.COMPLETED
  })

  const hasPendingQuiz = events?.some(({ id, status }) => {
    return id === GameEventId.QUIZ && status === EventStatus.PENDING
  })

  const didCollectAward = events?.some(({ id, status }) => {
    return id === GameEventId.QUIZ_CHEST && status === EventStatus.COMPLETED
  })

  const hasAdvancedQuiz = events?.some(({ id, status }) => {
    return id === GameEventId.ADVANCED_TOPIC_QUIZ && status === EventStatus.PENDING
  })

  const hasIntroDialogue = events?.some(({ id, status }) => {
    return id === GameEventId.INTRO && status === EventStatus.DIALOGUE
  })

  return {
    didCollectAward: didCollectAward ?? false,
    showAdvancedQuiz: (hasAdvancedQuiz && didCollectAward) ?? false,
    hasPendingQuiz: hasPendingQuiz ?? false,
    hasQuiz: hasQuiz ?? false,
    isLoading: !events,
    hasIntroDialogue,
  }
})

export const {
  setFunfact: setFunfactActionCreator,
  setMathEnergyOrbs: setMathEnergyOrbsActionCreator,
  setNotification: setNotificationActionCreator,
  setIsNotificationListOpen,
  setPoints: setPointsActionCreator,
  setHasLeveledUp,
  setHasLoggedOut,
  setShowHomebaseClosingX,
  setShowShopButton,
  setGems: setGemsActionCreator,
  setRewardsCount: setRewardsCountActionCreator,
  setNextBootcampPage: setNextBootcampPageActionCreator,
  updateEvents: updateEventsActionCreator,
  setEvents: setEventsActionCreator,
  resetGamification: resetGamificationActionCreator,
  setIsQuizFinished,
  setLeaderboardPosition,
  setHasShownLeaderboardModal,
} = slice.actions

export default slice.reducer
